import React, { useState, useEffect } from 'react';
import { Search, Clock, TrendingUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  mockResults,
  recentSearches,
  trendingSearches,
  getResultIcon,
  SearchResult,
} from './search-constants';
import { Card, CardContent } from '../ui/card';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length > 2) {
        setIsSearching(true);
        setTimeout(() => {
          setResults(
            mockResults.filter(
              (result) =>
                result.title.toLowerCase().includes(query.toLowerCase()) ||
                result.description.toLowerCase().includes(query.toLowerCase())
            )
          );
          setIsSearching(false);
        }, 300);
      } else {
        setResults([]);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [query]);

  const handleClose = () => {
    setQuery('');
    setResults([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="w-auto max-w-xl"
        aria-describedby="search-description"
        style={{
          borderRadius: '12px',
          background:
            'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(225, 230, 236, 0.5)',
          padding: '0',
          top: '20%',
          transform: 'translateY(-20%)',
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Search DomainBoost</DialogTitle>
          <DialogDescription id="search-description">
            Search for services, sites, articles, and other content across the platform
          </DialogDescription>
        </DialogHeader>

        {/* Search Input */}
        <div className="relative" style={{ padding: '16px 16px 0 16px' }}>
          <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#717182]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search services, sites, articles..."
            className="pl-10 border-0 shadow-none text-base"
            style={{
              height: '44px',
              paddingLeft: '40px',
              backgroundColor: 'transparent',
              border: 'none',
              boxShadow: 'none',
            }}
            autoFocus
            aria-label="Search DomainBoost platform"
          />
        </div>

        <div style={{ padding: '8px 16px 16px 16px' }}>
          {/* Search Results */}
          {query.length > 2 && (
            <>
              <Separator style={{ backgroundColor: '#E1E6EC', marginBottom: '12px' }} />
              <div className="space-y-2">
                {isSearching ? (
                  <div
                    className="text-[13px]"
                    style={{
                      color: '#717182',
                      textAlign: 'center',
                      padding: '8px',
                    }}
                  >
                    Searching...
                  </div>
                ) : results.length > 0 ? (
                  results.map((result) => {
                    const Icon = getResultIcon(result.type);
                    return (
                      <Card
                        key={result.id}
                        className="cursor-pointer hover:shadow-sm transition-all duration-200"
                        style={{
                          background: 'rgba(255, 255, 255, 0.7)',
                          border: '1px solid rgba(225, 230, 236, 0.6)',
                        }}
                      >
                        <CardContent style={{ padding: '8px 12px' }}>
                          <div className="flex items-center gap-3">
                            <Icon className="w-4 h-4 flex-shrink-0" style={{ color: '#4F46E5' }} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4
                                  className="text-[13px] font-semibold"
                                  style={{ color: '#1A202C' }}
                                >
                                  {result.title}
                                </h4>
                                {result.category && (
                                  <Badge
                                    variant="outline"
                                    className="text-[9px]"
                                    style={{ padding: '1px 4px' }}
                                  >
                                    {result.category}
                                  </Badge>
                                )}
                              </div>
                              <p
                                className="text-[11px]"
                                style={{ color: '#717182', lineHeight: '1.3' }}
                              >
                                {result.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <div
                    className="text-[13px]"
                    style={{
                      color: '#717182',
                      textAlign: 'center',
                      padding: '8px',
                    }}
                  >
                    No results found
                  </div>
                )}
              </div>
            </>
          )}

          {/* Recent & Trending - Only show when no search */}
          {!query && (
            <div className="space-y-3">
              {/* Recent Searches */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-3 h-3 text-[#717182]" />
                  <h4 className="text-xs font-semibold" style={{ color: '#1A202C' }}>
                    Recent
                  </h4>
                </div>
                <div className="flex flex-wrap gap-1">
                  {recentSearches.map((search) => (
                    <Badge
                      key={search}
                      variant="secondary"
                      onClick={() => setQuery(search)}
                      className="cursor-pointer hover:bg-primary/10 text-[10px]"
                      style={{ padding: '2px 6px' }}
                    >
                      {search}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Trending Searches */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-3 h-3 text-[#717182]" />
                  <h4 className="text-xs font-semibold" style={{ color: '#1A202C' }}>
                    Trending
                  </h4>
                </div>
                <div className="flex flex-wrap gap-1">
                  {trendingSearches.map((search) => (
                    <Badge
                      key={search}
                      variant="outline"
                      onClick={() => setQuery(search)}
                      className="cursor-pointer hover:bg-primary/10 text-[10px]"
                      style={{ padding: '2px 6px' }}
                    >
                      {search}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
